import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useLocation as useLocationContext } from '../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../contexts/BusinessTypeContext';
import { useAuth } from '../../contexts/AuthContext';
import apiClient, { getErrorMessage } from '../../utils/api';
import { Leave, LeaveStatus, Employee } from '../../types/api';
import { PlusIcon, CheckCircleIcon, XCircleIcon, XMarkIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

interface LeaveFormData {
  employee_id: string;
  leave_date: string;
  leave_type: string;
  reason?: string;
  business_type: 'fish' | 'goat';
}

const leaveSchema = yup.object({
  employee_id: yup.string().required('Employee is required'),
  leave_date: yup.string().required('Leave date is required'),
  leave_type: yup.string().required('Leave type is required'),
  reason: yup.string().optional(),
  business_type: yup.string().oneOf(['fish', 'goat']).required('Business type is required'),
});

const LeaveManagement: React.FC = () => {
  const { selectedLocation } = useLocationContext();
  const { businessType } = useBusinessTypeContext();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leaves, setLeaves] = useState<Leave[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | LeaveStatus>('all');

  const isAdmin = user?.role === 'admin';

  const form = useForm<LeaveFormData>({
    resolver: yupResolver(leaveSchema) as any,
    defaultValues: {
      employee_id: '',
      leave_date: new Date().toISOString().split('T')[0],
      leave_type: 'casual',
      reason: '',
      business_type: businessType,
    },
  });

  useEffect(() => {
    if (selectedLocation) {
      fetchLeaves();
      fetchEmployees();
    }
  }, [selectedLocation, statusFilter]); // Removed businessType dependency to show all business types

  const fetchEmployees = async () => {
    if (!selectedLocation) return;
    try {
      // Fetch all active employees regardless of business type
      const response = await apiClient.get<Employee[]>(`/employees?location_id=${selectedLocation.id}&is_active=true`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchLeaves = async () => {
    if (!selectedLocation) return;
    setLoading(true);
    try {
      // Fetch all leaves regardless of business type
      const params = new URLSearchParams({
        location_id: selectedLocation.id,
        ...(statusFilter !== 'all' && { status: statusFilter }),
      });
      const response = await apiClient.get<Leave[]>(`/leaves?${params.toString()}`);
      setLeaves(response.data);
    } catch (error) {
      console.error('Error fetching leaves:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data: LeaveFormData) => {
    if (!selectedLocation) {
      setFormError('Please select a location');
      return;
    }

    setFormError('');
    setSubmitting(true);

    try {
      await apiClient.post('/leaves', {
        employee_id: data.employee_id,
        location_id: selectedLocation.id,
        business_type: data.business_type,
        leave_date: data.leave_date,
        leave_type: data.leave_type,
        reason: data.reason || undefined,
      });

      setShowForm(false);
      form.reset();
      fetchLeaves();
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, 'Failed to create leave');
      setFormError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (leaveId: string) => {
    try {
      await apiClient.put(`/leaves/${leaveId}/approve`);
      fetchLeaves();
    } catch (error: any) {
      alert(getErrorMessage(error, 'Failed to approve leave'));
    }
  };

  const handleReject = async (leaveId: string) => {
    if (!window.confirm('Are you sure you want to reject this leave?')) return;
    try {
      await apiClient.put(`/leaves/${leaveId}/reject`);
      fetchLeaves();
    } catch (error: any) {
      alert(getErrorMessage(error, 'Failed to reject leave'));
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setFormError('');
    form.reset({
      employee_id: '',
      leave_date: new Date().toISOString().split('T')[0],
      leave_type: 'casual',
      reason: '',
      business_type: businessType,
    });
  };

  const getStatusColor = (status: LeaveStatus) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!selectedLocation) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">Please select a location to view leaves.</p>
        </div>
      </div>
    );
  }

  const filteredLeaves = statusFilter === 'all' ? leaves : leaves.filter(l => l.status === statusFilter);
  const regularEmployees = employees.filter(e => e.employee_type === 'manager' || e.employee_type === 'regular_farm_labour');

  return (
    <div className="p-3 sm:p-4 md:p-6">
      <div className="mb-4 sm:mb-6">
        <button
          onClick={() => navigate('/payroll')}
          className="mb-3 sm:mb-4 flex items-center text-gray-600 hover:text-gray-900 transition-colors text-sm sm:text-base"
        >
          <ArrowLeftIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
          <span>Back to Payroll</span>
        </button>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Leave Management</h1>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">Manage employee leaves</p>
          </div>
          {isAdmin && (
            <button
              onClick={() => setShowForm(true)}
              className="bg-blue-600 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2 w-full sm:w-auto"
            >
              <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="text-sm sm:text-base">Add Leave</span>
            </button>
          )}
        </div>
      </div>

      {/* Filter */}
      <div className="mb-4 overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
        <div className="flex space-x-2 min-w-max sm:min-w-0">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-2 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap ${
              statusFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setStatusFilter('pending')}
            className={`px-3 py-2 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap ${
              statusFilter === 'pending' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Pending
          </button>
          <button
            onClick={() => setStatusFilter('approved')}
            className={`px-3 py-2 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap ${
              statusFilter === 'approved' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Approved
          </button>
          <button
            onClick={() => setStatusFilter('rejected')}
            className={`px-3 py-2 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap ${
              statusFilter === 'rejected' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Rejected
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    {isAdmin && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredLeaves.map((leave) => (
                    <tr key={leave.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{leave.employee_name || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(leave.leave_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">{leave.leave_type}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{leave.reason || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(leave.status)}`}>
                          {leave.status.charAt(0).toUpperCase() + leave.status.slice(1)}
                        </span>
                      </td>
                      {isAdmin && leave.status === 'pending' && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <button
                            onClick={() => handleApprove(leave.id)}
                            className="text-green-600 hover:text-green-900"
                            title="Approve"
                          >
                            <CheckCircleIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleReject(leave.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Reject"
                          >
                            <XCircleIcon className="h-5 w-5" />
                          </button>
                        </td>
                      )}
                      {isAdmin && leave.status !== 'pending' && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">-</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredLeaves.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <p>No leaves found. {isAdmin && 'Click "Add Leave" to create one.'}</p>
              </div>
            )}
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {filteredLeaves.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
                <p className="text-sm">No leaves found. {isAdmin && 'Click "Add Leave" to create one.'}</p>
              </div>
            ) : (
              filteredLeaves.map((leave) => (
                <div key={leave.id} className="bg-white rounded-lg shadow p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-gray-900 mb-1">{leave.employee_name || 'N/A'}</h3>
                      <p className="text-xs text-gray-500">
                        {new Date(leave.leave_date).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(leave.status)} whitespace-nowrap ml-2`}>
                      {leave.status.charAt(0).toUpperCase() + leave.status.slice(1)}
                    </span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center">
                      <span className="text-gray-500 w-20">Type:</span>
                      <span className="text-gray-900 capitalize">{leave.leave_type}</span>
                    </div>
                    {leave.reason && (
                      <div className="flex items-start">
                        <span className="text-gray-500 w-20">Reason:</span>
                        <span className="text-gray-900 flex-1">{leave.reason}</span>
                      </div>
                    )}
                  </div>
                  {isAdmin && leave.status === 'pending' && (
                    <div className="mt-4 pt-4 border-t border-gray-200 flex space-x-3">
                      <button
                        onClick={() => handleApprove(leave.id)}
                        className="flex-1 bg-green-50 text-green-700 px-4 py-2 rounded-lg hover:bg-green-100 transition-colors flex items-center justify-center space-x-2 text-sm font-medium"
                      >
                        <CheckCircleIcon className="h-4 w-4" />
                        <span>Approve</span>
                      </button>
                      <button
                        onClick={() => handleReject(leave.id)}
                        className="flex-1 bg-red-50 text-red-700 px-4 py-2 rounded-lg hover:bg-red-100 transition-colors flex items-center justify-center space-x-2 text-sm font-medium"
                      >
                        <XCircleIcon className="h-4 w-4" />
                        <span>Reject</span>
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* Add Leave Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h2 className="text-lg sm:text-xl font-bold">Add Leave</h2>
              <button
                onClick={handleCloseForm}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            </div>

            {formError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
                <p className="text-red-800 text-xs sm:text-sm">{formError}</p>
              </div>
            )}

            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                {/* Employee */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employee <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...form.register('employee_id')}
                    className={`w-full px-3 py-2 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      form.formState.errors.employee_id ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select employee</option>
                    {regularEmployees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name} ({emp.employee_code})
                      </option>
                    ))}
                  </select>
                  {form.formState.errors.employee_id && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600">{form.formState.errors.employee_id.message}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">Only regular employees (Manager/Regular Farm Labour) can take leaves</p>
                </div>

                {/* Business Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Business Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...form.register('business_type')}
                    className={`w-full px-3 py-2 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      form.formState.errors.business_type ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="fish">Fish</option>
                    <option value="goat">Goat</option>
                  </select>
                  {form.formState.errors.business_type && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600">{form.formState.errors.business_type.message}</p>
                  )}
                </div>

                {/* Leave Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Leave Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    {...form.register('leave_date')}
                    className={`w-full px-3 py-2 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      form.formState.errors.leave_date ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {form.formState.errors.leave_date && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600">{form.formState.errors.leave_date.message}</p>
                  )}
                </div>

                {/* Leave Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Leave Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...form.register('leave_type')}
                    className={`w-full px-3 py-2 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      form.formState.errors.leave_type ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="casual">Casual</option>
                    <option value="sick">Sick</option>
                    <option value="annual">Annual</option>
                    <option value="other">Other</option>
                  </select>
                  {form.formState.errors.leave_type && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600">{form.formState.errors.leave_type.message}</p>
                  )}
                </div>

                {/* Reason */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason
                  </label>
                  <input
                    type="text"
                    {...form.register('reason')}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional reason"
                  />
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 pt-4 border-t mt-4 sm:mt-6">
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors w-full sm:w-auto"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 w-full sm:w-auto"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Creating...</span>
                    </>
                  ) : (
                    <span>Create Leave</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveManagement;
